import {isNilOrEmpty} from 'ramda-adjunct';

import {SMART_SEARCH_KEY} from '../constants/smartSearchKey';
import type {GetDataQueryResponse} from '../types/api/data';
import type {Filters} from '../types/Filters';
import {normalizeFilterValues} from './normalizeFilterValues';

/**
 * Builds the store's wrapped `filterValues` from a data query: the normalized
 * column filters plus (when present) the smart-search value nested under
 * SMART_SEARCH_KEY.
 *
 * This is THE canonical dataQuery → filterValues seeding used both by
 * useFilterHydration (reactive re-seed) and by usePresetActivation (synchronous
 * seed on preset switch, so the engine's next fetch never reads the PREVIOUS
 * preset's filters — the custom→Default filter-leak fix, T20-134468).
 *
 * `smartSearchOverride` is the optional consumer-supplied deep-link search term
 * (see useFilterHydration's `initialSmartSearch`); an empty string means "not
 * provided" and must not shadow the query's persisted smart search.
 */
export function buildFilterValuesFromDataQuery(
  dataQuery: GetDataQueryResponse,
  smartSearchOverride?: string
): Filters {
  const smartSearchValue = isNilOrEmpty(smartSearchOverride)
    ? dataQuery.smartSearch
    : smartSearchOverride;

  return {
    ...normalizeFilterValues(dataQuery.filters),
    ...(smartSearchValue ? {[SMART_SEARCH_KEY]: {[SMART_SEARCH_KEY]: smartSearchValue}} : {}),
  };
}
